/** Routing and customer identity are independent, even when their values match. */
export type CallIdentity = {
  customerPhone: string | null;
  dialNumber: string | null;
};

export type SoftphoneCallOptions = { customerPhone?: string | null };

function nullablePhone(value: unknown, field: keyof CallIdentity): string | null {
  if (value == null) return null;
  if (typeof value !== "string") throw new TypeError(`${field} must be a string or null`);
  return value.trim() || null;
}

/** Full snapshots always spell out unknown values. Never derive one field from the other. */
export function callIdentity(value?: Partial<CallIdentity> | null): CallIdentity {
  return {
    customerPhone: nullablePhone(value?.customerPhone, "customerPhone"),
    dialNumber: nullablePhone(value?.dialNumber, "dialNumber")
  };
}

/** Omission preserves this call's identity; explicit null clears it. No shared cache. */
export function updateCallIdentity(call: Partial<CallIdentity>, update: Partial<CallIdentity>): void {
  for (const field of ["customerPhone", "dialNumber"] as const) {
    if (Object.prototype.hasOwnProperty.call(update, field) && update[field] !== undefined) {
      call[field] = nullablePhone(update[field], field);
    }
  }
}

export function callEventIdentity(call: CallIdentity | null, detail: Partial<CallIdentity>): Partial<CallIdentity> {
  const identity: Partial<CallIdentity> = call ? callIdentity(call) : {};
  updateCallIdentity(identity, detail);
  return identity;
}

export function incomingCustomerPhone(invitation): string | null {
  const user = invitation?.remoteIdentity?.uri?.user || invitation?.request?.from?.uri?.user;
  // A display name or an anonymous SIP identifier is not a known caller phone.
  return typeof user === "string" && /^\+?\d+$/.test(user) ? user : null;
}

/** Gateway channel context travels with this INVITE, never with "the next call". */
export function incomingCallContext(invitation) {
  const value = invitation?.request?.getHeader?.("X-DoCRM-Call-Context");
  if (value === undefined || value === null) return null;
  if (typeof value !== "string" || value.length > 2048 || !/^[A-Za-z0-9_-]+$/.test(value))
    throw new TypeError("Invalid gateway call context");
  const bytes = Uint8Array.from(atob(value.replace(/-/g, "+").replace(/_/g, "/")), char => char.charCodeAt(0));
  const context = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  if (context.version !== 1 || !Object.hasOwn(context, "customerPhone") ||
      (context.customerPhone !== null && (typeof context.customerPhone !== "string" ||
        context.customerPhone.length > 128 || /[\x00-\x1f\x7f]/.test(context.customerPhone))) ||
      !["transfer", "conference_invite"].includes(context.purpose) ||
      typeof context.sourceExtension !== "string" || !/^[A-Za-z0-9_-]{1,128}$/.test(context.sourceExtension) ||
      (context.purpose === "conference_invite" && (typeof context.room !== "string" || !/^\d+$/.test(context.room))))
    throw new TypeError("Invalid gateway call context");
  return { customerPhone: nullablePhone(context.customerPhone, "customerPhone"),
    transfer: { type: context.purpose, sourceExtension: context.sourceExtension,
      ...(context.purpose === "conference_invite" ? { room: context.room } : {}) } };
}
